import { Download } from '@mui/icons-material'
import { Skeleton } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { formatDateTimeShort, getLocaleFromFullDate } from 'common/libs/fechas'
import Calendario from 'features/calendario/pages/Calendario'
import { HojasGastoLinPage } from 'features/hojasgasto/pages/HojasGastoLinPage'
import { selectUsuario } from 'features/users/stores/userSlice'
import i18n from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import useChatSolicitud from '../hooks/useChatSolicitud'
import { translateMask } from '../libs/DateFieldsHelpers'
import { getChipSolicitud } from '../libs/SolicitudesHelpers'
import { selectSolicitudSeleccionada, setSolicitudSeleccionada } from '../solicitudesSlice'
import CabeceraSolicitud from './CabeceraSolicitud'
import ChatSolicitud from './ChatSolicitud'
import DatosSolicitudSkeleton from './skeletons/DatosSolicitudSkeleton'

const DatosSolicitud = ({ solicitud, campos }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const seleccionada = useSelector(selectSolicitudSeleccionada)
  const user = useSelector(selectUsuario)

  let fechasSolicitud
  if (solicitud && solicitud.id_solicitud_pe === 'VAC01') {
    fechasSolicitud = solicitud.datos
      .map((d) => {
        if (d.valor_date) return d.valor_date
      })
      .filter((f) => f)
  }

  useEffect(() => {
    if (!solicitud) {
      if (location?.state?.solicitud) {
        dispatch(setSolicitudSeleccionada(location.state.solicitud))
        return
      }
      navigate('/listasolicitudes/')
    }
  }, [dispatch, navigate, location, solicitud, seleccionada])

  const handleDownload = (e) => {
    var link = document.createElement('a')
    link.download = e.currentTarget.dataset.nombre
    link.href = e.currentTarget.dataset.url
    link.click()
  }

  if (!solicitud) return 'Cargando'

  const renderDatos = () => (
    <Card>
      <CardHeader title="Datos" />
      <CardContent>
        {solicitud.id_solicitud_pe === 'VAC01' && fechasSolicitud ? (
          <Calendario
            requestUser={solicitud.usuario_creacion ? solicitud.usuario_creacion : null}
            vistaAmpliada={false}
            fechasSolicitud={fechasSolicitud}
            requestCode={solicitud.codigo_solicitud ? solicitud.codigo_solicitud : null}
            requestOrigin={solicitud.origen_solicitud ? solicitud.origen_solicitud : null}
          />
        ) : null}
        {solicitud.id_solicitud_pe === 'VALHG' ? (
          <HojasGastoLinPage
            id_h={parseInt(
              solicitud.datos.filter(
                (campo) => campo.codigo_campo === solicitud.cod_campo_id_hoja,
              )[0].valor_number,
            )}
            encr={solicitud.encr}
            disableAll={true}
          />
        ) : null}
        {!campos || (campos && campos.length === 0) ? (
          <DatosSolicitudSkeleton />
        ) : (
          <List>
            {campos.map((c) => {
              if (c.visible !== 'S') return null
              const dato = solicitud.datos.filter(
                (d) =>
                  d.codigo_plantilla === c.codigo_plantilla && d.codigo_campo === c.codigo_campo,
              )[0]
              if (!dato) return null
              let valor = null
              let secondary = null
              switch (c.tipo_dato) {
                case 'V':
                  valor = dato.valor_alfa
                  break
                case 'N':
                  valor = Number(dato.valor_number).toFixed(c.required_decimals) ?? ''
                  break
                case 'D':
                case 'DH1':
                case 'DH2': {
                  const { options } = translateMask(c.mascara_formato)
                  valor = dato.valor_date
                    ? getLocaleFromFullDate(dato.valor_date, true, 'es-Es', options)
                    : ''
                  break
                }
                case 'S':
                  return (
                    <Divider textAlign="left" sx={{ marginTop: 2, marginBottom: 1 }}>
                      <Chip label={c.etiqueta} />
                    </Divider>
                  )
                case 'B':
                  secondary = (
                    <IconButton edge="end" aria-label="comments">
                      <Download />
                    </IconButton>
                  )
                  break
                default:
                  break
              }
              if (c.tipo_dato === 'B') {
                if (dato.valor_file) {
                  return (
                    <>
                      <ListItem key={c.id} disablePadding>
                        <ListItemButton
                          sx={{ height: 56 }}
                          data-url={dato.valor_file}
                          data-nombre={dato.d_valor ?? valor}
                          onClick={handleDownload}
                        >
                          <ListItemIcon>
                            <Download color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={dato.d_valor ?? valor}
                            secondary={c.etiqueta}
                            sx={{ display: 'flex', flexDirection: 'column-reverse' }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider component="li" />
                    </>
                  )
                } else {
                  return <></>
                }
              }
              return (
                <>
                  <ListItem key={c.id}>
                    <ListItemText
                      primary={dato.d_valor ?? valor}
                      secondary={c.etiqueta}
                      sx={{ display: 'flex', flexDirection: 'column-reverse' }}
                    />
                  </ListItem>
                  <Divider component="li" />
                </>
              )
            })}
          </List>
        )}
      </CardContent>
    </Card>
  )

  const renderHistorico = () => (
    <Card>
      <CardHeader title="Histórico" />
      <CardContent>
        <List>
          {!solicitud?.estados ? (
            <>
              <ListItem key={1}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={45} height={45} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="rounded" width={100} height={20} sx={{ mb: 1 }} />}
                  secondary={<Skeleton variant="rounded" width={200} height={20} />}
                />
              </ListItem>
              <ListItem key={2}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={45} height={45} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="rounded" width={100} height={20} sx={{ mb: 1 }} />}
                  secondary={<Skeleton variant="rounded" width={200} height={20} />}
                />
              </ListItem>
              <ListItem key={3}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={45} height={45} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="rounded" width={100} height={20} sx={{ mb: 1 }} />}
                  secondary={<Skeleton variant="rounded" width={200} height={20} />}
                />
              </ListItem>
            </>
          ) : (
            solicitud.estados?.map((e) => {
              const status_fecha = formatDateTimeShort(e.fecha_hora)
              return (
                <>
                  <ListItem key={e.id} secondaryAction={''}>
                    <ListItemAvatar>
                      <Avatar
                        alt={e.d_usuario?.split(', ')[1]}
                        src={`/assets/images/${e.img_usuario}`}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={e.d_usuario}
                      secondary={
                        <>
                          <Typography
                            sx={{ display: 'block' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {status_fecha}
                          </Typography>
                          {getChipSolicitud(Number(e.status), e.d_status)}
                          {e.status_texto ? ` - ${e.status_texto}` : ''}
                        </>
                      }
                    />
                  </ListItem>
                </>
              )
            })
          )}
        </List>
      </CardContent>
    </Card>
  )

  if (isDesktop) {
    return (
      <Grid container spacing={2} data-testid="datos-solicitud-grid">
        <Grid key="cabecera" size={{ xs: 12 }}>
          <CabeceraSolicitud descripcion={solicitud.d_tipo} solicitud={solicitud} />
        </Grid>
        <Grid key="datos" size={{ xs: 12, sm: 12, md: 8 }}>
          {renderDatos()}
        </Grid>
        <Grid key="stausychat" size={{ xs: 12, sm: 12, md: 4 }}>
          <Stack spacing={2}>
            {renderHistorico()}
            <ChatSolicitud
              codigoSolicitud={solicitud.codigo_solicitud}
              origenSolicitud={solicitud.origen_solicitud}
              estado={solicitud.estado}
            />
          </Stack>
        </Grid>
      </Grid>
    )
  }

  return (
    <DatosSolicitudMobile
      solicitud={solicitud}
      renderDatos={renderDatos}
      renderHistorico={renderHistorico}
    />
  )
}

const DatosSolicitudMobile = ({ solicitud, renderDatos, renderHistorico }) => {
  const [tab, setTab] = useState('datos')
  const { mensajes } = useChatSolicitud({
    codigoSolicitud: solicitud.codigo_solicitud,
    origenSolicitud: solicitud.origen_solicitud,
  })
  const usuarioActual = useSelector(selectUsuario)
  const usuarioWeb = usuarioActual?.usuario_web

  const noLeidosCount = useMemo(
    () =>
      (mensajes || []).filter((m) => !m.leido && (!usuarioWeb || m.usuario_web !== usuarioWeb))
        .length,
    [mensajes, usuarioWeb],
  )

  const handleChange = (_e, nuevo) => setTab(nuevo)

  return (
    <Stack spacing={2}>
      <CabeceraSolicitud descripcion={solicitud.d_tipo} solicitud={solicitud} />
      <Tabs value={tab} onChange={handleChange} variant="fullWidth">
        <Tab value="datos" label={i18n.t('tabdatsl', { defaultValue: 'Datos' })} />
        <Tab value="historico" label={i18n.t('tabhistsl', { defaultValue: 'Histórico' })} />
        <Tab
          value="chat"
          label={
            <Badge
              badgeContent={noLeidosCount}
              color="primary"
              max={99}
              invisible={noLeidosCount === 0}
            >
              <Box component="span" sx={{ px: 1 }}>
                {i18n.t('tabchatsl', { defaultValue: 'Chat' })}
              </Box>
            </Badge>
          }
        />
      </Tabs>
      <Box sx={{ display: tab === 'datos' ? 'block' : 'none' }}>{renderDatos()}</Box>
      <Box sx={{ display: tab === 'historico' ? 'block' : 'none' }}>{renderHistorico()}</Box>
      <Box sx={{ display: tab === 'chat' ? 'block' : 'none' }}>
        <ChatSolicitud
          codigoSolicitud={solicitud.codigo_solicitud}
          origenSolicitud={solicitud.origen_solicitud}
          estado={solicitud.estado}
        />
      </Box>
    </Stack>
  )
}

export default DatosSolicitud
